/* eslint sort-keys: "error" */
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { or } from 'ember-truth-helpers';

import type {
  IconColor,
  IconName,
  IconSize,
} from '../../types/components/prism/icon.ts';
import PrismIcon from './icon.gts';
import PrismLoadingSpinner from './loading-spinner.gts';

type ButtonSize = 'large' | 'medium' | 'small';

type ButtonType =
  | 'brand'
  | 'critical'
  | 'critical-secondary'
  | 'ghost'
  | 'intelligence'
  | 'intelligence-secondary'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'unstyled'
  | 'warning';

const buttonSizeToIconSize: Record<ButtonSize, IconSize> = {
  large: 'large',
  medium: 'medium',
  small: 'small',
};

const buttonTypeToIconColor: Record<ButtonType, IconColor> = {
  brand: 'default-inverse',
  critical: 'default-inverse',
  'critical-secondary': 'critical',
  ghost: 'default',
  intelligence: 'default-inverse',
  'intelligence-secondary': 'on-intelligence',
  primary: 'default-inverse',
  secondary: 'default',
  success: 'default-inverse',
  unstyled: 'default',
  warning: 'on-warning',
};

interface PrismButtonSignature {
  Args: {
    buttonType?: ButtonType;
    /** Icon name, to be used in conjunction with the button-type="icon" attribute */
    icon?: IconName;
    /** Icon to be rendered after the value attribute */
    iconAfter?: IconName;
    /** Icon to be rendered before the value attribute */
    iconBefore?: IconName;
    /** Disabled flag. If true, button is disabled. */
    isDisabled?: boolean;
    /** Loading state */
    isLoading?: boolean;
    /** isButtonSmall flag. If true, button will have small paddings and font size */
    /**
     * @deprecated use size instead
     */
    isSmall?: boolean;
    /** Value defines the text in the button  */
    label?: string;
    loadingMessage?: string;
    onClick?: (event: MouseEvent) => unknown;
    pseudo?: 'active' | 'focus' | 'hover';
    size?: ButtonSize;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement;
}

export default class PrismButton extends Component<PrismButtonSignature> {
  get classesForButton(): string {
    const { buttonType, isLoading, isSmall, label, size } = this.args;
    const classes = ['prism-button'];

    classes.push(`prism-button--${buttonType ?? 'primary'}`);

    if (isSmall) {
      classes.push('prism-button--small');
    } else {
      classes.push(`prism-button--${size ?? 'medium'}`);
    }

    if (!label) {
      classes.push('prism-button--icon-only');
    }

    if (isLoading) {
      classes.push('prism-button--loading');
    }

    return classes.join(' ');
  }

  get iconColor(): IconColor {
    const { buttonType, isDisabled } = this.args;

    if (isDisabled) {
      return 'disabled';
    }

    return buttonTypeToIconColor[buttonType ?? 'primary'];
  }

  get iconSize(): IconSize {
    const { isSmall, label, size } = this.args;

    if (isSmall || label) {
      return 'small';
    }

    return buttonSizeToIconSize[size ?? 'medium'];
  }

  @action onClick(event: MouseEvent): void {
    this.args.onClick?.(event);
  }

  <template>
    <button
      aria-disabled={{or @isDisabled @isLoading}}
      class={{this.classesForButton}}
      data-test-button={{@label}}
      data-test-pseudo={{@pseudo}}
      disabled={{@isDisabled}}
      type="button"
      {{on "click" this.onClick}}
      ...attributes
    >
      {{#if @iconBefore}}
        <PrismIcon
          @color={{this.iconColor}}
          @name={{@iconBefore}}
          @size={{this.iconSize}}
        />
      {{/if}}

      {{#if @label}}
        <span class="truncate">{{@label}}</span>
      {{/if}}

      {{#if @icon}}
        <PrismIcon
          @color={{this.iconColor}}
          @name={{@icon}}
          @size={{this.iconSize}}
        />
      {{/if}}

      {{#if @iconAfter}}
        <PrismIcon
          @color={{this.iconColor}}
          @name={{@iconAfter}}
          @size={{this.iconSize}}
        />
      {{/if}}

      {{yield}}

      {{#if @isLoading}}
        <span aria-live="polite" class="prism-button--loading-message">
          {{@loadingMessage}}
        </span>
        <span class="prism-button--loading-spinner">
          <PrismLoadingSpinner @color="inherit" @size={{this.iconSize}} />
        </span>
      {{/if}}
    </button>
  </template>
}
